import React, { useEffect, useRef, useState } from "react";
import { useMessaging } from "../../context/MessagingContext";
import { useNavigate } from "react-router-dom";

const GroupMembersDropdown = ({
  isOpen,
  onClose,
  members = [],
  buttonRef,
  messagesPath = "/artist/messages",
}) => {
  const { getOrCreateConversation } = useMessaging();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Calculate dropdown position based on button position
  useEffect(() => {
    if (isOpen && buttonRef?.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 350;
      const dropdownHeight = 400;

      let right = window.innerWidth - buttonRect.right;
      if (buttonRect.right + dropdownWidth > window.innerWidth) {
        right = window.innerWidth - buttonRect.right;
      }
      if (right + dropdownWidth > window.innerWidth) {
        right = Math.max(8, window.innerWidth - dropdownWidth - 8);
      }

      let top = buttonRect.bottom + 8;
      if (top + dropdownHeight > window.innerHeight) {
        top = buttonRect.top - dropdownHeight - 8;
        if (top < 0) {
          top = 8;
        }
      }

      setPosition({ top, right });
    }
  }, [isOpen, buttonRef]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef?.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose, buttonRef]);

  const handleSelectMember = async (member) => {
    try {
      setLoading(true);
      setError(null);
      const conversation = await getOrCreateConversation(member.user_id);
      if (conversation) {
        onClose();
        const base = messagesPath.replace(/\?.*$/, "").replace(/\/$/, "");
        navigate(`${base}?conversation=${conversation.conversation_id}`);
      }
    } catch (err) {
      console.error("Error creating conversation:", err);
      setError(err.message || "Failed to start conversation");
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "owner":
        return <span className="recipient-role-badge owner">Owner</span>;
      case "member":
        return <span className="recipient-role-badge admin">Member</span>;
      default:
        return null;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="bar-contacts-dropdown"
      ref={dropdownRef}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        right: `${position.right}px`,
        zIndex: 2000,
      }}
    >
      <div className="new-message-dropdown-header">
        <h3>Message</h3>
        <button
          type="button"
          className="new-message-dropdown-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="new-message-dropdown-list">
        {loading ? (
          <div className="new-message-loading">
            <div className="spinner"></div>
            <p>Starting conversation...</p>
          </div>
        ) : error ? (
          <div className="new-message-error">
            <p>{error}</p>
          </div>
        ) : members.length === 0 ? (
          <div className="new-message-empty">
            <p>No members available</p>
          </div>
        ) : (
          members.map((member) => (
            <div
              key={member.user_id}
              className="new-message-recipient-item"
              onClick={() => handleSelectMember(member)}
            >
              <div className="recipient-avatar">
                {(member.name || member.email)?.charAt(0).toUpperCase() || "?"}
              </div>
              <div className="recipient-info">
                <div className="recipient-name-row">
                  <span className="recipient-name">
                    {member.name || member.email}
                  </span>
                  {getRoleBadge(member.role)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GroupMembersDropdown;
